import { Placement } from '@floating-ui/react';
import { Libraries } from '@react-google-maps/api';
import {
  AccessoryCategoryType,
  AccessoryTypeType,
  ActiveTabType,
  AirHandlerTypeCodeType,
  AirHandlerTypeLabelType,
  AttributeCodeType,
  ComparisonFeatureType,
  ComparisonTableDataType,
  ExistingSystemComponentType,
  ExposureDirectionForCompassType,
  ExposureDirectionType,
  LevelDetailsLabelKey,
  SystemFiltersType
} from 'data/types';

export const tabs: ActiveTabType[] = [
  'project',
  'existingEquipment',
  'equipmentPreferences',
  'structure',
  'configurations',
  'chooseSystems',
  'accessories',
  'quote'
];

export const tabLabels: { [tab in ActiveTabType]: string } = {
  project: 'Project Details',
  existingEquipment: 'Existing Equipment',
  equipmentPreferences: 'Equipment Preferences',
  structure: 'Layout & Sizing',
  configurations: 'System Design',
  chooseSystems: 'Choose Systems',
  accessories: 'Add-Ons',
  quote: 'Quote'
};

export const accessoryCategories: {
  [type in AccessoryCategoryType]?: AccessoryTypeType[];
} = {
  lineSet: ['line_set', 'wire'],
  ductingKit: [
    'concealed_duct_installation_kit',
    'ducting_kit_supply',
    'ducting_kit_return',
    'plenum'
  ],
  mounting: [
    'pad',
    'wall_bracket',
    'mounting_blocks',
    'slab_stand',
    'mighty_bracket'
  ],
  power: [
    'electrical_disconnect',
    'electrical_whips',
    'electrical_whip',
    'surge_protector'
  ],
  install: [
    'adapter',
    'brass_nut',
    'brass_union',
    'brass_union_nut_kit',
    'end_cap',
    'drain_hose',
    'drain_line_fitting',
    'condensate_pump',
    'expander',
    'flaring_tool',
    'grille',
    'sealant'
  ],
  control: ['controls', 'remote', 'wifi'],
  lineSetCover: ['line_set_cover'],
  ductingTransitionKit: ['ducting_transitionKit'],
  support: ['equipmentSupport'],
  humidifier: ['humidifier_bypass', 'humidifier_powerFan', 'humidifier_steam'],
  ventilation: [
    'uvPurifier',
    'ventilation_advancedSystem',
    'ventilation_basicSystem',
    'ventilation_installationKit'
  ]
};

export const ductlessAccessoryCategories: AccessoryCategoryType[] = [
  'mounting',
  'power',
  'install',
  'control'
];

export const ductedAccessoryCategories: AccessoryCategoryType[] = [
  'power',
  'ductingTransitionKit',
  'support',
  'humidifier',
  'ventilation'
];

export const advancedAccessoryCategories: AccessoryCategoryType[] = [
  'lineSetCover'
];

export const accessoryCategoryProductTypes: {
  [type in AccessoryCategoryType]?: 'air handler' | 'condenser';
} = {
  mounting: 'condenser',
  power: 'condenser',
  install: 'air handler',
  control: 'air handler',
  ductingTransitionKit: 'air handler',
  support: 'condenser',
  humidifier: 'air handler',
  ventilation: 'air handler',
  lineSetCover: 'air handler'
};

export const accessoryGroupLabels: {
  [type in AccessoryCategoryType]?: {
    singular: string;
    plural: string;
    description: {
      singular: string;
      plural: string;
    };
  };
} = {
  mounting: {
    singular: 'Mounting the Condenser',
    plural: 'Mounting the Condenser',
    description: {
      singular: "Let's discuss how the condenser will be mounted outside.",
      plural: "Let's discuss how the condensers will be mounted outside."
    }
  },
  power: {
    singular: 'Powering the Condenser',
    plural: 'Powering the Condenser',
    description: {
      singular:
        "Let's go over the standard electrical needs for powering and maintaining the condenser.",
      plural:
        "Let's go over the standard electrical needs for powering and maintaining the condensers."
    }
  },
  install: {
    singular: 'Installing the Components',
    plural: 'Installing the Components',
    description: {
      singular:
        'There are a few additional accessories needed for a hassle-free installation.',
      plural:
        'There are a few additional accessories needed for a hassle-free installation.'
    }
  },
  control: {
    singular: 'Controlling the System',
    plural: 'Controlling the System',
    description: {
      singular:
        "When it comes to setting the temperature, are there accessories that you'll need us to include?",
      plural:
        "When it comes to setting the temperature, are there accessories that you'll need us to include?"
    }
  },
  lineSetCover: {
    singular: 'Covering the Line Set',
    plural: 'Covering the Line Sets',
    description: {
      singular:
        'Line Set Covers are good for protecting your Line Set from the elements.',
      plural:
        'Line Set Covers are good for protecting your Line Sets from the elements.'
    }
  },
  ductingTransitionKit: {
    singular: 'Ducting Transition Kit',
    plural: 'Ducting Transition Kit',
    description: {
      singular:
        'Ducting Transition Kits are used to connect ducting to the air handler.',
      plural:
        'Ducting Transition Kits are used to connect ducting to the air handlers.'
    }
  },
  support: {
    singular: 'Equipment Support',
    plural: 'Equipment Support',
    description: {
      singular: 'Equipment Supports are used to support the air handler.',
      plural: 'Equipment Supports are used to support the air handlers.'
    }
  },
  humidifier: {
    singular: 'Humidifier',
    plural: 'Humidifier',
    description: {
      singular: 'Humidifiers are used to add moisture to the air.',
      plural: 'Humidifiers are used to add moisture to the air.'
    }
  },
  ventilation: {
    singular: 'Ventilation',
    plural: 'Ventilation',
    description: {
      singular:
        'Ventilation is used to circulate fresh air throughout the home.',
      plural: 'Ventilation is used to circulate fresh air throughout the home.'
    }
  },
  advanced: {
    singular: 'Advanced Accessory',
    plural: 'Advanced Accessories',
    description: {
      singular: 'Advanced accessory is available for specific needs.',
      plural: 'Advanced accessories are available for specific needs.'
    }
  }
};

export const airHandlerTypeCodes: {
  [type in AirHandlerTypeLabelType]: AirHandlerTypeCodeType;
} = {
  'High Wall': 'wall',
  'Ceiling Cassette Standard': 'cassette',
  'Low Wall': 'heatingConsole',
  'Floor Ceiling Console': 'console',
  'Multi Room': 'concealed',
  'High Wall DIY': 'wallDIY',
  'Ceiling Cassette Slim Fit': 'cassette_16',
  'Ceiling Cassette Drop Ceiling': 'cassette_drop_ceiling',
  BMAH: 'ducted',
  'High Static Multi Room': 'concealed_high_static',
  'Next Generation Evaporator Coil': 'evap_coil'
};

export const airHandlerTypeAbbreviations: {
  [type in AirHandlerTypeLabelType]: string[];
} = {
  'High Wall': ['WM', 'WMV'],
  'Ceiling Cassette Standard': ['CC4', 'CC8'],
  'Low Wall': ['LW'],
  'Floor Ceiling Console': ['FC'],
  'Multi Room': ['CDL'],
  'High Wall DIY': ['WM', 'WMV'],
  'Ceiling Cassette Slim Fit': ['CC1'],
  'Ceiling Cassette Drop Ceiling': ['CC4', 'CC8'],
  BMAH: ['DH', 'DH2', 'DH3'],
  'High Static Multi Room': ['CDH'],
  'Next Generation Evaporator Coil': ['ENA', 'ENB', 'ENC', 'END']
};

export const airHandlerSizes: number[] = [
  6, 9, 12, 16, 18, 24, 30, 33, 36, 48, 60
];

export const condenserSizes: number[] = [6, 12, 18, 24, 30, 36, 48, 60];

export const dimensionsAttributeCodes: AttributeCodeType[] = [
  'ceiling_height',
  'ceiling_type',
  'vaulted_height',
  'sloped_height'
];

export const ceilingHeightPresets: number[] = [8, 9, 10];

export const peakHeightPresets: number[] = [10, 12, 14];

export const levelDetailsQuestionLabels: Record<LevelDetailsLabelKey, string> =
  {
    conditioned: 'Is this level currently conditioned?',
    heating: 'Are you adding heating to this level?',
    cooling: 'Are you adding cooling to this level?',
    ceiling_height: "What is this level's ceiling height?",
    level_ceiling_type: "What is this level's ceiling type?",
    sloped_height: "What is the sloped ceiling's peak height?",
    vaulted_height: "What is the vaulted ceiling's peak height?",
    crawlspace_height: "What is the crawlspace's height?",
    level_ductwork: 'Does this level have existing ductwork?',
    crawlspace_ductwork: 'Does the crawlspace have existing ductwork?'
  };

export const proceedErrorMessages: { [tab in ActiveTabType]: string } = {
  project: 'Please complete all required fields before proceeding.',
  existingEquipment:
    'Add at least one piece of existing equipment to continue.',
  equipmentPreferences:
    'Complete Equipment Preferences — tell us what type of equipment you want.',
  structure: 'Please complete the structure before proceeding.',
  configurations:
    'Please finish your system design and wait for valid options before proceeding.',
  chooseSystems: 'Please add at least one system to compare before proceeding.',
  accessories: 'Please select all your accessories before proceeding.',
  quote: 'Please select a quote to send before proceeding.'
};

export const tabGatingMessages = {
  noSystemsToCompare:
    'There are no systems to compare. Go to Choose Systems and select at least one system to compare.',
  noQuotableSystems:
    'There are no systems to accessorize. Go to Choose Systems and add at least one system to your comparison.',
  configurationsLockedDuctedOnly:
    "System Design isn't needed for one ducted system on a single level — your systems come straight from what you told us on Existing Equipment. Continue to Choose Systems.",
  // Hybrid with no mini-split rooms: the whole-house ducted configuration is
  // generated in the background (utils/system-design-bypass).
  configurationsLockedHybridWholeHouse:
    "System Design isn't needed until you add a mini-split room — a whole-house ducted configuration is generated for you. Continue to Choose Systems."
};

export const confidenceGateMessages = {
  notifyBanner: (overall: number, threshold: number) =>
    `Confidence is ${overall}%, below the recommended ${threshold}% minimum. You can still send quotes, but confirming the customer's equipment details first is recommended.`
};

export const chooseSystemsEmptyMessages = {
  noCompleteConfiguration:
    'Complete a configuration on the System Design page to see available systems.',
  // Ducted pathway (AHA-6681): recommendations come from Existing Equipment,
  // not System Design — steering there would be wrong on this path.
  noDuctedRecommendations:
    'Tell us what you have and what you want on the Existing Equipment page to see compatible systems.',
  // Hybrid with no mini-split rooms: System Design is hidden, so the only
  // thing that can be missing is the background whole-house configuration
  // (it needs at least one room on Layout & Sizing to exist).
  hybridWholeHousePending:
    'Your whole-house ducted configuration is generated from Layout & Sizing — add your rooms there to see available systems.',
  // Hotel Style (PTAC): the systems are sized per zone off the System
  // Design, so an incomplete design (an unplaced unit, no rooms) steers there.
  noPtacDesign:
    'Place a unit in every zone on the System Design page to see the PTAC systems that fit.',
  noMatchingSystems: 'No systems match your filters.'
};

export const adjustmentFactors = [
  {
    heatCoolRatio: 2.0,
    insulation: {
      poor: {
        heatingFactor: 1.25,
        coolingFactor: 0.8
      },
      good: {
        heatingFactor: 1.0,
        coolingFactor: 0.6
      },
      great: {
        heatingFactor: 0.75,
        coolingFactor: 0.5
      }
    }
  },
  {
    heatCoolRatio: 1.35,
    insulation: {
      poor: {
        heatingFactor: 1.0,
        coolingFactor: 1.0
      },
      good: {
        heatingFactor: 0.8,
        coolingFactor: 0.8
      },
      great: {
        heatingFactor: 0.6,
        coolingFactor: 0.6
      }
    }
  },
  {
    heatCoolRatio: 1.0,
    insulation: {
      poor: {
        heatingFactor: 0.8,
        coolingFactor: 1.25
      },
      good: {
        heatingFactor: 0.6,
        coolingFactor: 1.0
      },
      great: {
        heatingFactor: 0.5,
        coolingFactor: 0.75
      }
    }
  },
  {
    heatCoolRatio: 0,
    insulation: {
      poor: {
        heatingFactor: 0.6,
        coolingFactor: 1.6
      },
      good: {
        heatingFactor: 0.5,
        coolingFactor: 1.3
      },
      great: {
        heatingFactor: 0.4,
        coolingFactor: 1.0
      }
    }
  }
];

export const systemFilterLabels: {
  [type in keyof SystemFiltersType]: string;
} = {
  inStock: 'Show only in-stock systems',
  belowBudget: `Stay below customer's budget`,
  selectedAirHandlers: 'Show only chosen air handler types',
  isOnSale: 'Show only discounted systems'
};

export const comparisonFeatureDependencies: {
  [key in ComparisonFeatureType]: (keyof ComparisonTableDataType)[];
} = {
  Price: ['price'],
  'Potential Rebates': ['totalRebates'],
  'Price After Rebates': ['price', 'totalRebates'],
  'Heating Coverage': ['heatingScore', 'condenserCoverage'],
  'Cooling Coverage': ['coolingScore', 'condenserCoverage'],
  'Average Efficiency': ['averageSeer'],
  'Total Units to Install': ['totalUnitsToInstall'],
  'Indoor Units': ['totalIndoorUnits'],
  'Indoor Visibility': ['indoorVisibility'],
  'Outdoor Units': ['totalOutdoorUnits'],
  'Outdoor Visibility': ['outdoorVisibility'],
  'Installation Difficulty': ['installationDifficulty'],
  Amperage: ['amperage']
};

export const exposureOptions: ExposureDirectionType[] = [
  'North',
  'East',
  'South',
  'West',
  'North East',
  'North West',
  'South East',
  'South West'
];

export const exposureOptionsWithHorizontal: ExposureDirectionForCompassType[] =
  [...exposureOptions, 'Horizontal'];

export const exposureOptionsForCompass: (ExposureDirectionType | 'Center')[] = [
  'North West',
  'North',
  'North East',
  'West',
  'Center',
  'East',
  'South West',
  'South',
  'South East'
];

export const exposureOptionsForCompassWithHorizontal: ExposureDirectionForCompassType[] =
  [...exposureOptionsForCompass, 'Horizontal'];

export const compassDirectionDegrees: {
  [key in ExposureDirectionForCompassType]: number;
} = {
  'North West': 315,
  North: 0,
  'North East': 45,
  West: 270,
  Center: 0,
  East: 90,
  'South West': 225,
  South: 180,
  'South East': 135,
  Horizontal: 0
};

export const compassDirectionTooltipPlacements: {
  [key in ExposureDirectionForCompassType]: Placement;
} = {
  'North West': 'top-end',
  North: 'top',
  'North East': 'top-start',
  West: 'left',
  Center: 'top',
  East: 'right',
  'South West': 'bottom-end',
  South: 'bottom',
  'South East': 'bottom-start',
  Horizontal: 'top'
};

export const aboveGradeGlassTypes = [
  'Small Window',
  'Medium Window',
  'Large Window',
  'Sliding Glass Door',
  'Bay Window',
  'French Door'
] as const;

export const belowGradeGlassTypes = aboveGradeGlassTypes;

export const doorTypes = ['Wood Door', 'Metal Door'] as const;

type FormInputConfig = {
  label: string;
  type:
    | 'text'
    | 'number'
    | 'select'
    | 'multiselect'
    | 'toggleSwitch'
    | 'checkbox';
  options?: any[];
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  required?: boolean;
  isBoolean?: boolean;
  matching?: boolean;
  enableChips?: boolean;
};

type ComponentPropertiesConfig = {
  [K in Exclude<ExistingSystemComponentType['type'], undefined>]: Record<
    string,
    FormInputConfig
  >;
};

export const REFRIGERANT_OPTIONS = [
  'R-410A',
  'R-32',
  'R-454B',
  'R-22',
  'R-407C',
  'R-404A',
  'R-134A'
];

// Plain letter codes only (Geoff ruling, AHA-6681 spec-field reorg): no
// width-hint labels, no free entry.
export const CHASSIS_SIZE_OPTIONS = ['A', 'B', 'C', 'D', 'E', 'F', 'N'];

const STAGES_OPTIONS = ['One', 'Two', 'Inverter'];

const ORIENTATION_OPTIONS = ['upflow', 'downflow', 'horizontal'];

const FUEL_TYPE_OPTIONS = ['Electricity', 'Natural Gas', 'Propane (LP)', 'Oil'];

const YES_NO_OPTIONS = ['Yes', 'No'];

const DUCTLESS_PRODUCT_CODE_OPTIONS = ['WM', 'LW', 'FC', 'CD', 'CC'];

const VOLTAGE_PHASE_OPTIONS = [
  '208-230V / 1-phase',
  '208-230V / 3-phase',
  '460V / 3-phase',
  'Other'
];

const VENTING_MATERIAL_OPTIONS = [
  'Metal vent (80% AFUE class)',
  'PVC / plastic vent (90%+ condensing)'
];

const HEAT_KIT_BREAKER_SIZE_OPTIONS = [
  '30A',
  '40A',
  '50A',
  '60A',
  '70A+',
  'No heat kit',
  'Unknown'
];

const GAS_TYPE_OPTIONS = ['Natural Gas', 'Propane (LP)'];

const PTAC_BTU_CLASS_OPTIONS = [
  '7,000 BTU',
  '9,000 BTU',
  '12,000 BTU',
  '15,000 BTU',
  'Other'
];

const PTAC_VOLTAGE_OPTIONS = ['208/230V', '265/277V'];

const PTAC_AMPERAGE_OPTIONS = ['15A', '20A', '30A'];

const POWER_CORD_CONFIG_OPTIONS = [
  'Pre-attached',
  'Field-selectable',
  'Unknown'
];

export const existingSystemComponentTypeProperties: Partial<ComponentPropertiesConfig> =
  {
    'Split System Condenser': {
      manufacturer: { label: 'Manufacturer', type: 'text' },
      manufactureDate: { label: 'Manufacture Date', type: 'text' },
      refrigerantType: {
        label: 'Refrigerant Type',
        type: 'select',
        options: REFRIGERANT_OPTIONS,
        required: true,
        matching: true
      },
      tonnage: {
        label: 'Tonnage',
        type: 'number',
        step: 0.5,
        required: true,
        matching: true
      },
      coolingCapacity: {
        label: 'Cooling Capacity (BTU)',
        type: 'number',
        min: 0
      },
      seer2Min: { label: 'SEER2 Min', type: 'number', min: 0 },
      seer2Max: { label: 'SEER2 Max', type: 'number', min: 0 },
      eer2Min: { label: 'EER2 Min', type: 'number', min: 0 },
      eer2Max: { label: 'EER2 Max', type: 'number', min: 0 },
      stages: {
        label: 'Stages',
        type: 'select',
        options: STAGES_OPTIONS,
        matching: true
      },
      voltagePhase: {
        label: 'Voltage / Phase',
        type: 'select',
        options: VOLTAGE_PHASE_OPTIONS
      },
      isCommunicating: {
        label: 'Communicating?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      txvRequired: {
        label: 'TXV Required?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      mobileHomeCompatible: {
        label: 'Mobile Home Compatible?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      }
    },
    'Split System Heat Pump': {
      manufacturer: { label: 'Manufacturer', type: 'text' },
      manufactureDate: { label: 'Manufacture Date', type: 'text' },
      refrigerantType: {
        label: 'Refrigerant Type',
        type: 'select',
        options: REFRIGERANT_OPTIONS,
        required: true,
        matching: true
      },
      tonnage: {
        label: 'Tonnage',
        type: 'number',
        step: 0.5,
        required: true,
        matching: true
      },
      coolingCapacity: {
        label: 'Cooling Capacity (BTU)',
        type: 'number',
        min: 0
      },
      heatingCapacity: {
        label: 'Heating Capacity (BTU)',
        type: 'number',
        min: 0
      },
      seer2Min: { label: 'SEER2 Min', type: 'number', min: 0 },
      seer2Max: { label: 'SEER2 Max', type: 'number', min: 0 },
      eer2Min: { label: 'EER2 Min', type: 'number', min: 0 },
      eer2Max: { label: 'EER2 Max', type: 'number', min: 0 },
      hspf2Min: { label: 'HSPF2 Min', type: 'number', min: 0 },
      hspf2Max: { label: 'HSPF2 Max', type: 'number', min: 0 },
      cop5Min: { label: 'COP @ 5°F Min', type: 'number', min: 0 },
      cop5Max: { label: 'COP @ 5°F Max', type: 'number', min: 0 },
      stages: {
        label: 'Stages',
        type: 'select',
        options: STAGES_OPTIONS,
        matching: true
      },
      voltagePhase: {
        label: 'Voltage / Phase',
        type: 'select',
        options: VOLTAGE_PHASE_OPTIONS
      },
      isCommunicating: {
        label: 'Communicating?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      txvRequired: {
        label: 'TXV Required?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      mobileHomeCompatible: {
        label: 'Mobile Home Compatible?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      }
    },
    'Split System Furnace': {
      manufacturer: { label: 'Manufacturer', type: 'text' },
      manufactureDate: { label: 'Manufacture Date', type: 'text' },
      cabinetChassisSize: {
        label: 'Cabinet Chassis Size',
        type: 'select',
        options: CHASSIS_SIZE_OPTIONS,
        required: true,
        matching: true
      },
      heatingCapacity: {
        label: 'Heating Capacity (BTU)',
        type: 'number',
        min: 0,
        required: true
      },
      afue: {
        label: 'AFUE (%)',
        type: 'number',
        min: 0,
        max: 100,
        step: 0.5
      },
      ventingMaterial: {
        label: 'Venting Material',
        type: 'select',
        options: VENTING_MATERIAL_OPTIONS
      },
      fuelType: {
        label: 'Fuel Type',
        type: 'select',
        options: FUEL_TYPE_OPTIONS,
        required: true,
        matching: true,
        // Keep the segmented control now that a 4th option (Propane (LP))
        // exceeds FormInput's <=3 auto-chip threshold (AHA-6796).
        enableChips: true
      },
      orientation: {
        label: 'Orientation',
        type: 'select',
        options: ORIENTATION_OPTIONS,
        required: true,
        matching: true
      },
      minBlowerCFM: {
        label: 'Min Blower CFM',
        type: 'number',
        min: 0,
        matching: true
      },
      maxBlowerCFM: {
        label: 'Max Blower CFM',
        type: 'number',
        min: 0,
        matching: true
      },
      multiStageCompressorCompatible: {
        label: 'Multi-Stage Compressor Compatible?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true,
        matching: true
      },
      isCommunicating: {
        label: 'Communicating?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      rdsOnBoard: {
        label: 'RDS On Board?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      rdsProgrammedFor: {
        label: 'RDS Programmed For',
        type: 'text'
      },
      loNOx: {
        label: 'Lo-NOx?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      ultraLoNOx: {
        label: 'Ultra Lo-NOx?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      mobileHomeCompatible: {
        label: 'Mobile Home Compatible?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true,
        matching: true
      }
    },
    'Split System Evaporator Coil': {
      manufacturer: { label: 'Manufacturer', type: 'text' },
      manufactureDate: { label: 'Manufacture Date', type: 'text' },
      cabinetChassisSize: {
        label: 'Cabinet Chassis Size',
        type: 'select',
        options: CHASSIS_SIZE_OPTIONS,
        matching: true
      },
      minTonnage: {
        label: 'Min Tonnage',
        type: 'number',
        step: 0.5,
        min: 0,
        matching: true
      },
      maxTonnage: {
        label: 'Max Tonnage',
        type: 'number',
        step: 0.5,
        min: 0,
        matching: true
      },
      primaryRefrigerant: {
        label: 'Primary Refrigerant',
        type: 'select',
        options: REFRIGERANT_OPTIONS,
        required: true,
        matching: true
      },
      refrigerantFamilySupport: {
        label: 'Refrigerant Family Support',
        type: 'multiselect',
        options: REFRIGERANT_OPTIONS
      },
      orientation: {
        label: 'Orientation',
        type: 'select',
        options: ORIENTATION_OPTIONS,
        matching: true
      },
      builtInTXV: {
        label: 'Built-In TXV?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true,
        matching: true
      },
      refrigerantMeteringDevice: {
        label: 'Refrigerant Metering Device',
        type: 'text'
      },
      meteringDeviceCompatibility: {
        label: 'Metering Device Compatibility',
        type: 'text'
      },
      a2lLeakSensorIncluded: {
        label: 'A2L Leak Sensor Included?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      mobileHomeCompatible: {
        label: 'Mobile Home Compatible?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true,
        matching: true
      }
    },
    'Split System Air Handler': {
      manufacturer: { label: 'Manufacturer', type: 'text' },
      manufactureDate: { label: 'Manufacture Date', type: 'text' },
      cabinetChassisSize: {
        label: 'Cabinet Chassis Size',
        type: 'select',
        options: CHASSIS_SIZE_OPTIONS,
        required: true
      },
      minBlowerCapacity: {
        label: 'Min Blower Capacity (CFM)',
        type: 'number',
        min: 0,
        matching: true
      },
      maxBlowerCapacity: {
        label: 'Max Blower Capacity (CFM)',
        type: 'number',
        min: 0,
        matching: true
      },
      orientation: {
        label: 'Orientation',
        type: 'select',
        options: ORIENTATION_OPTIONS,
        required: true
      },
      heatKitBreakerSize: {
        label: 'Heat Kit Breaker Size',
        type: 'select',
        options: HEAT_KIT_BREAKER_SIZE_OPTIONS,
        required: true
      },
      primaryRefrigerant: {
        label: 'Primary Refrigerant',
        type: 'select',
        options: REFRIGERANT_OPTIONS,
        required: true,
        matching: true
      },
      refrigerantFamilySupport: {
        label: 'Refrigerant Family Support',
        type: 'multiselect',
        options: REFRIGERANT_OPTIONS
      },
      isCommunicating: {
        label: 'Communicating?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      multiStageCompressorCompatible: {
        label: 'Multi-Stage Compressor Compatible?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true,
        matching: true
      },
      rdsOnBoard: {
        label: 'RDS On Board?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      rdsProgrammedFor: {
        label: 'RDS Programmed For',
        type: 'text'
      },
      refrigerantMeteringDevice: {
        label: 'Refrigerant Metering Device',
        type: 'text'
      },
      meteringDeviceCompatibility: {
        label: 'Metering Device Compatibility',
        type: 'text'
      },
      a2lLeakSensorIncluded: {
        label: 'A2L Leak Sensor Included?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      mobileHomeCompatible: {
        label: 'Mobile Home Compatible?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true,
        matching: true
      }
    },
    'Split System Modular Blower': {
      manufacturer: { label: 'Manufacturer', type: 'text' },
      manufactureDate: { label: 'Manufacture Date', type: 'text' },
      cabinetChassisSize: {
        label: 'Cabinet Chassis Size',
        type: 'select',
        options: CHASSIS_SIZE_OPTIONS,
        matching: true
      },
      minBlowerCapacity: {
        label: 'Min Blower Capacity (CFM)',
        type: 'number',
        min: 0,
        matching: true
      },
      maxBlowerCapacity: {
        label: 'Max Blower Capacity (CFM)',
        type: 'number',
        min: 0,
        matching: true
      },
      orientation: {
        label: 'Orientation',
        type: 'select',
        options: ORIENTATION_OPTIONS,
        matching: true
      },
      primaryRefrigerant: {
        label: 'Primary Refrigerant',
        type: 'select',
        options: REFRIGERANT_OPTIONS,
        matching: true
      },
      refrigerantFamilySupport: {
        label: 'Refrigerant Family Support',
        type: 'multiselect',
        options: REFRIGERANT_OPTIONS
      },
      isCommunicating: {
        label: 'Communicating?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      multiStageCompressorCompatible: {
        label: 'Multi-Stage Compressor Compatible?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true,
        matching: true
      },
      rdsOnBoard: {
        label: 'RDS On Board?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      rdsProgrammedFor: {
        label: 'RDS Programmed For',
        type: 'text'
      },
      a2lLeakSensorIncluded: {
        label: 'A2L Leak Sensor Included?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      mobileHomeCompatible: {
        label: 'Mobile Home Compatible?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true,
        matching: true
      }
    },
    'Self Contained Electric AC': {
      manufacturer: { label: 'Manufacturer', type: 'text' },
      manufactureDate: { label: 'Manufacture Date', type: 'text' },
      tonnage: {
        label: 'Tonnage',
        type: 'number',
        step: 0.5,
        required: true,
        matching: true
      },
      coolingCapacity: {
        label: 'Cooling Capacity (BTU)',
        type: 'number',
        min: 0
      },
      refrigerant: {
        label: 'Refrigerant',
        type: 'select',
        options: REFRIGERANT_OPTIONS,
        matching: true
      },
      voltagePhase: {
        label: 'Voltage / Phase',
        type: 'select',
        options: VOLTAGE_PHASE_OPTIONS,
        required: true
      },
      seer2Min: { label: 'SEER2 Min', type: 'number', min: 0 },
      seer2Max: { label: 'SEER2 Max', type: 'number', min: 0 },
      eer2Min: { label: 'EER2 Min', type: 'number', min: 0 },
      eer2Max: { label: 'EER2 Max', type: 'number', min: 0 },
      loNOx: {
        label: 'Lo-NOx?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      ultraLoNOx: {
        label: 'Ultra Lo-NOx?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      heatKitBreakerSize: {
        label: 'Heat Kit Breaker Size',
        type: 'select',
        options: HEAT_KIT_BREAKER_SIZE_OPTIONS,
        required: true
      },
      compressorCircuitAmperage: {
        label: 'Compressor Circuit Amperage',
        type: 'number',
        min: 0
      },
      footprintLength: {
        label: 'Cabinet Footprint Length (in)',
        type: 'number',
        min: 0,
        required: true
      },
      footprintWidth: {
        label: 'Cabinet Footprint Width (in)',
        type: 'number',
        min: 0,
        required: true
      },
      curbHeight: {
        label: 'Curb Height (in)',
        type: 'number',
        min: 0,
        required: true
      },
      ductOpeningWidth: {
        label: 'Duct Opening Width (in)',
        type: 'number',
        min: 0,
        required: true
      },
      ductOpeningLength: {
        label: 'Duct Opening Length (in)',
        type: 'number',
        min: 0,
        required: true
      }
    },
    'Self Contained Electric Heat Pump': {
      manufacturer: { label: 'Manufacturer', type: 'text' },
      manufactureDate: { label: 'Manufacture Date', type: 'text' },
      tonnage: {
        label: 'Tonnage',
        type: 'number',
        step: 0.5,
        required: true,
        matching: true
      },
      coolingCapacity: {
        label: 'Cooling Capacity (BTU)',
        type: 'number',
        min: 0
      },
      heatingCapacity: {
        label: 'Heating Capacity (BTU)',
        type: 'number',
        min: 0
      },
      refrigerant: {
        label: 'Refrigerant',
        type: 'select',
        options: REFRIGERANT_OPTIONS,
        matching: true
      },
      voltagePhase: {
        label: 'Voltage / Phase',
        type: 'select',
        options: VOLTAGE_PHASE_OPTIONS,
        required: true
      },
      seer2Min: { label: 'SEER2 Min', type: 'number', min: 0 },
      seer2Max: { label: 'SEER2 Max', type: 'number', min: 0 },
      eer2Min: { label: 'EER2 Min', type: 'number', min: 0 },
      eer2Max: { label: 'EER2 Max', type: 'number', min: 0 },
      hspf2Min: { label: 'HSPF2 Min', type: 'number', min: 0 },
      hspf2Max: { label: 'HSPF2 Max', type: 'number', min: 0 },
      cop5Min: { label: 'COP @ 5°F Min', type: 'number', min: 0 },
      cop5Max: { label: 'COP @ 5°F Max', type: 'number', min: 0 },
      loNOx: {
        label: 'Lo-NOx?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      ultraLoNOx: {
        label: 'Ultra Lo-NOx?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      heatKitBreakerSize: {
        label: 'Heat Kit Breaker Size',
        type: 'select',
        options: HEAT_KIT_BREAKER_SIZE_OPTIONS,
        required: true
      },
      compressorCircuitAmperage: {
        label: 'Compressor Circuit Amperage',
        type: 'number',
        min: 0
      },
      footprintLength: {
        label: 'Cabinet Footprint Length (in)',
        type: 'number',
        min: 0,
        required: true
      },
      footprintWidth: {
        label: 'Cabinet Footprint Width (in)',
        type: 'number',
        min: 0,
        required: true
      },
      curbHeight: {
        label: 'Curb Height (in)',
        type: 'number',
        min: 0,
        required: true
      },
      ductOpeningWidth: {
        label: 'Duct Opening Width (in)',
        type: 'number',
        min: 0,
        required: true
      },
      ductOpeningLength: {
        label: 'Duct Opening Length (in)',
        type: 'number',
        min: 0,
        required: true
      }
    },
    'Self Contained Gas Pack With AC': {
      manufacturer: { label: 'Manufacturer', type: 'text' },
      manufactureDate: { label: 'Manufacture Date', type: 'text' },
      tonnage: {
        label: 'Tonnage',
        type: 'number',
        step: 0.5,
        required: true,
        matching: true
      },
      coolingCapacity: {
        label: 'Cooling Capacity (BTU)',
        type: 'number',
        min: 0
      },
      heatingCapacity: {
        label: 'Heating Capacity (BTU)',
        type: 'number',
        min: 0
      },
      gasType: {
        label: 'Gas Type',
        type: 'select',
        options: GAS_TYPE_OPTIONS,
        required: true
      },
      refrigerant: {
        label: 'Refrigerant',
        type: 'select',
        options: REFRIGERANT_OPTIONS,
        matching: true
      },
      voltagePhase: {
        label: 'Voltage / Phase',
        type: 'select',
        options: VOLTAGE_PHASE_OPTIONS,
        required: true
      },
      seer2Min: { label: 'SEER2 Min', type: 'number', min: 0 },
      seer2Max: { label: 'SEER2 Max', type: 'number', min: 0 },
      eer2Min: { label: 'EER2 Min', type: 'number', min: 0 },
      eer2Max: { label: 'EER2 Max', type: 'number', min: 0 },
      loNOx: {
        label: 'Lo-NOx?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      ultraLoNOx: {
        label: 'Ultra Lo-NOx?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      compressorCircuitAmperage: {
        label: 'Compressor Circuit Amperage',
        type: 'number',
        min: 0
      },
      footprintLength: {
        label: 'Cabinet Footprint Length (in)',
        type: 'number',
        min: 0,
        required: true
      },
      footprintWidth: {
        label: 'Cabinet Footprint Width (in)',
        type: 'number',
        min: 0,
        required: true
      },
      curbHeight: {
        label: 'Curb Height (in)',
        type: 'number',
        min: 0,
        required: true
      },
      ductOpeningWidth: {
        label: 'Duct Opening Width (in)',
        type: 'number',
        min: 0,
        required: true
      },
      ductOpeningLength: {
        label: 'Duct Opening Length (in)',
        type: 'number',
        min: 0,
        required: true
      }
    },
    'Self Contained Gas Pack With Heat Pump': {
      manufacturer: { label: 'Manufacturer', type: 'text' },
      manufactureDate: { label: 'Manufacture Date', type: 'text' },
      tonnage: {
        label: 'Tonnage',
        type: 'number',
        step: 0.5,
        required: true,
        matching: true
      },
      coolingCapacity: {
        label: 'Cooling Capacity (BTU)',
        type: 'number',
        min: 0
      },
      heatingCapacity: {
        label: 'Heating Capacity (BTU)',
        type: 'number',
        min: 0
      },
      gasType: {
        label: 'Gas Type',
        type: 'select',
        options: GAS_TYPE_OPTIONS,
        required: true
      },
      refrigerant: {
        label: 'Refrigerant',
        type: 'select',
        options: REFRIGERANT_OPTIONS,
        matching: true
      },
      voltagePhase: {
        label: 'Voltage / Phase',
        type: 'select',
        options: VOLTAGE_PHASE_OPTIONS,
        required: true
      },
      seer2Min: { label: 'SEER2 Min', type: 'number', min: 0 },
      seer2Max: { label: 'SEER2 Max', type: 'number', min: 0 },
      eer2Min: { label: 'EER2 Min', type: 'number', min: 0 },
      eer2Max: { label: 'EER2 Max', type: 'number', min: 0 },
      hspf2Min: { label: 'HSPF2 Min', type: 'number', min: 0 },
      hspf2Max: { label: 'HSPF2 Max', type: 'number', min: 0 },
      cop5Min: { label: 'COP @ 5°F Min', type: 'number', min: 0 },
      cop5Max: { label: 'COP @ 5°F Max', type: 'number', min: 0 },
      loNOx: {
        label: 'Lo-NOx?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      ultraLoNOx: {
        label: 'Ultra Lo-NOx?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      compressorCircuitAmperage: {
        label: 'Compressor Circuit Amperage',
        type: 'number',
        min: 0
      },
      footprintLength: {
        label: 'Cabinet Footprint Length (in)',
        type: 'number',
        min: 0,
        required: true
      },
      footprintWidth: {
        label: 'Cabinet Footprint Width (in)',
        type: 'number',
        min: 0,
        required: true
      },
      curbHeight: {
        label: 'Curb Height (in)',
        type: 'number',
        min: 0,
        required: true
      },
      ductOpeningWidth: {
        label: 'Duct Opening Width (in)',
        type: 'number',
        min: 0,
        required: true
      },
      ductOpeningLength: {
        label: 'Duct Opening Length (in)',
        type: 'number',
        min: 0,
        required: true
      }
    },
    'Ductless Air Handler': {
      manufacturer: { label: 'Manufacturer', type: 'text' },
      manufactureDate: { label: 'Manufacture Date', type: 'text' },
      productCode: {
        label: 'Product Code',
        type: 'select',
        options: DUCTLESS_PRODUCT_CODE_OPTIONS,
        required: true,
        matching: true
      },
      refrigerant: {
        label: 'Refrigerant',
        type: 'select',
        options: REFRIGERANT_OPTIONS,
        matching: true
      },
      rdsOnBoard: {
        label: 'RDS On Board?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true,
        matching: true
      },
      rdsProgrammedFor: {
        label: 'RDS Programmed For',
        type: 'text',
        matching: true
      },
      a2lLeakSensorIncluded: {
        label: 'A2L Leak Sensor Included?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true,
        matching: true
      },
      mobileHomeCompatible: {
        label: 'Mobile Home Compatible?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true,
        matching: true
      }
    },
    'Next-Gen Heat Pump': {
      manufacturer: { label: 'Manufacturer', type: 'text' },
      manufactureDate: { label: 'Manufacture Date', type: 'text' },
      tonnage: {
        label: 'Tonnage',
        type: 'number',
        step: 0.5,
        required: true,
        matching: true
      },
      refrigerantCompatibility: {
        label: 'Refrigerant Compatibility',
        type: 'select',
        options: REFRIGERANT_OPTIONS,
        matching: true
      },
      stages: {
        label: 'Stages',
        type: 'select',
        options: STAGES_OPTIONS,
        matching: true
      },
      txvRequired: {
        label: 'TXV Required?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      coolingCapacity: {
        label: 'Cooling Capacity (BTU)',
        type: 'number',
        min: 0
      },
      heatingCapacity: {
        label: 'Heating Capacity (BTU)',
        type: 'number',
        min: 0
      },
      seer2Min: { label: 'SEER2 Min', type: 'number', min: 0 },
      seer2Max: { label: 'SEER2 Max', type: 'number', min: 0 },
      eer2Min: { label: 'EER2 Min', type: 'number', min: 0 },
      eer2Max: { label: 'EER2 Max', type: 'number', min: 0 },
      hspf2Min: { label: 'HSPF2 Min', type: 'number', min: 0 },
      hspf2Max: { label: 'HSPF2 Max', type: 'number', min: 0 },
      cop5Min: { label: 'COP @ 5°F Min', type: 'number', min: 0 },
      cop5Max: { label: 'COP @ 5°F Max', type: 'number', min: 0 },
      mobileHomeCompatible: {
        label: 'Mobile Home Compatible?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      }
    },
    'Next-Gen Condenser': {
      manufacturer: { label: 'Manufacturer', type: 'text' },
      manufactureDate: { label: 'Manufacture Date', type: 'text' },
      tonnage: {
        label: 'Tonnage',
        type: 'number',
        step: 0.5,
        required: true,
        matching: true
      },
      refrigerantCompatibility: {
        label: 'Refrigerant Compatibility',
        type: 'select',
        options: REFRIGERANT_OPTIONS,
        matching: true
      },
      stages: {
        label: 'Stages',
        type: 'select',
        options: STAGES_OPTIONS,
        matching: true
      },
      txvRequired: {
        label: 'TXV Required?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      coolingCapacity: {
        label: 'Cooling Capacity (BTU)',
        type: 'number',
        min: 0
      },
      seer2Min: { label: 'SEER2 Min', type: 'number', min: 0 },
      seer2Max: { label: 'SEER2 Max', type: 'number', min: 0 },
      eer2Min: { label: 'EER2 Min', type: 'number', min: 0 },
      eer2Max: { label: 'EER2 Max', type: 'number', min: 0 },
      mobileHomeCompatible: {
        label: 'Mobile Home Compatible?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      }
    },
    'Next-Gen Evaporator Coil': {
      manufacturer: { label: 'Manufacturer', type: 'text' },
      manufactureDate: { label: 'Manufacture Date', type: 'text' },
      cabinetChassisSize: {
        label: 'Cabinet Chassis Size',
        type: 'select',
        options: CHASSIS_SIZE_OPTIONS,
        matching: true
      },
      minTonnage: {
        label: 'Min Tonnage',
        type: 'number',
        step: 0.5,
        min: 0,
        matching: true
      },
      maxTonnage: {
        label: 'Max Tonnage',
        type: 'number',
        step: 0.5,
        min: 0,
        matching: true
      },
      primaryRefrigerant: {
        label: 'Primary Refrigerant',
        type: 'select',
        options: REFRIGERANT_OPTIONS,
        matching: true
      },
      refrigerantFamilySupport: {
        label: 'Refrigerant Family Support',
        type: 'multiselect',
        options: REFRIGERANT_OPTIONS
      },
      orientation: {
        label: 'Orientation',
        type: 'select',
        options: ORIENTATION_OPTIONS,
        matching: true
      },
      builtInTXV: {
        label: 'Built-In TXV?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true,
        matching: true
      },
      refrigerantMeteringDevice: {
        label: 'Refrigerant Metering Device',
        type: 'text'
      },
      meteringDeviceCompatibility: {
        label: 'Metering Device Compatibility',
        type: 'text'
      },
      a2lLeakSensorIncluded: {
        label: 'A2L Leak Sensor Included?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      mobileHomeCompatible: {
        label: 'Mobile Home Compatible?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true,
        matching: true
      }
    },
    'Next-Gen Air Handler': {
      manufacturer: { label: 'Manufacturer', type: 'text' },
      manufactureDate: { label: 'Manufacture Date', type: 'text' },
      minBlowerCapacity: {
        label: 'Min Blower Capacity (CFM)',
        type: 'number',
        min: 0,
        matching: true
      },
      maxBlowerCapacity: {
        label: 'Max Blower Capacity (CFM)',
        type: 'number',
        min: 0,
        matching: true
      },
      multiStageCompressorCompatible: {
        label: 'Multi-Stage Compressor Compatible?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true,
        matching: true
      },
      rdsOnBoard: {
        label: 'RDS On Board?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      rdsProgrammedFor: {
        label: 'RDS Programmed For',
        type: 'text'
      },
      refrigerantMeteringDevice: {
        label: 'Refrigerant Metering Device',
        type: 'text'
      },
      meteringDeviceCompatibility: {
        label: 'Metering Device Compatibility',
        type: 'text'
      },
      a2lLeakSensorIncluded: {
        label: 'A2L Leak Sensor Included?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true
      },
      mobileHomeCompatible: {
        label: 'Mobile Home Compatible?',
        type: 'select',
        options: YES_NO_OPTIONS,
        isBoolean: true,
        matching: true
      }
    },
    'Packaged Terminal (PTAC)': {
      manufacturer: { label: 'Manufacturer', type: 'text' },
      manufactureDate: { label: 'Manufacture Date', type: 'text' },
      tonnage: {
        label: 'Tonnage',
        type: 'number',
        step: 0.5,
        required: true,
        matching: true
      },
      btuClass: {
        label: 'BTU Class',
        type: 'select',
        options: PTAC_BTU_CLASS_OPTIONS,
        required: true
      },
      coolingCapacity: {
        label: 'Cooling Capacity (BTU)',
        type: 'number',
        min: 0
      },
      heatingCapacity: {
        label: 'Heating Capacity (BTU)',
        type: 'number',
        min: 0
      },
      refrigerant: {
        label: 'Refrigerant',
        type: 'select',
        options: REFRIGERANT_OPTIONS,
        matching: true
      },
      voltage: {
        label: 'Voltage',
        type: 'select',
        options: PTAC_VOLTAGE_OPTIONS,
        required: true
      },
      amperage: {
        label: 'Amperage',
        type: 'select',
        options: PTAC_AMPERAGE_OPTIONS,
        required: true
      },
      seer2Min: { label: 'SEER2 Min', type: 'number', min: 0 },
      seer2Max: { label: 'SEER2 Max', type: 'number', min: 0 },
      eer2Min: { label: 'EER2 Min', type: 'number', min: 0 },
      eer2Max: { label: 'EER2 Max', type: 'number', min: 0 },
      hspf2Min: { label: 'HSPF2 Min', type: 'number', min: 0 },
      hspf2Max: { label: 'HSPF2 Max', type: 'number', min: 0 },
      cop5Min: { label: 'COP @ 5°F Min', type: 'number', min: 0 },
      cop5Max: { label: 'COP @ 5°F Max', type: 'number', min: 0 },
      sleeveWidth: {
        label: 'Sleeve Width (in)',
        type: 'number',
        min: 0,
        required: true
      },
      sleeveHeight: {
        label: 'Sleeve Height (in)',
        type: 'number',
        min: 0,
        required: true
      },
      sleeveDepth: {
        label: 'Sleeve Depth (in)',
        type: 'number',
        min: 0,
        required: true
      },
      powerCordConfig: {
        label: 'Power Cord Configuration',
        type: 'select',
        options: POWER_CORD_CONFIG_OPTIONS
      }
    }
  };

export const STRUCTURE_GRID_SIZE = 10;

export const EQUIPMENT_GRID_SQUARES = 3;

export const MIN_SPACE_DIM_FEET = 2;

export const MAX_ZONES_PER_CONDENSER = 6;

export const IMAGE_ANALYZING_MESSAGES = [
  'Reviewing the property images you uploaded...',
  'Tracing the outline of the structure...',
  'Identifying windows, doors, and exterior walls...',
  'Estimating dimensions and footprint from the photos...',
  'Detecting roof shape and orientation...',
  'Mapping each visible level...',
  'Cross-checking image findings against property records...',
  'Translating what we found into the structure layout...',
  'Almost ready — opening your structure for review...'
];

export const STRUCTURE_GENERATING_MESSAGES = [
  'Pulling property records for this address...',
  'Reading the year built, lot size, and building shape...',
  'Counting the number of stories on the structure...',
  'Checking for a basement, crawlspace, or slab...',
  'Looking for an attic, garage, or other unconditioned spaces...',
  'Estimating square footage for each level...',
  'Setting default ceiling heights and wall framing...',
  'Applying your structure type and coverage selections...',
  'Laying out each level in the structure...',
  'Almost ready — opening your structure for review...'
];

export const EDGE_TO_EXPOSURE_MAP: Record<string, string> = {
  top: 'North',
  right: 'East',
  bottom: 'South',
  left: 'West'
};

export const SUN_DIRECTION_EDGE_TO_ORIENTATION: Record<
  string,
  ExposureDirectionType
> = {
  top: 'East',
  right: 'South',
  bottom: 'West',
  left: 'North'
};

export const rotationToOrientation: Record<number, ExposureDirectionType> = {
  0: 'North',
  45: 'North East',
  90: 'East',
  135: 'South East',
  180: 'South',
  225: 'South West',
  270: 'West',
  315: 'North West'
};

export const orientationToRotation: Record<ExposureDirectionType, number> = {
  North: 0,
  'North East': 45,
  East: 90,
  'South East': 135,
  South: 180,
  'South West': 225,
  West: 270,
  'North West': 315
};

export const directionAbbreviations: Record<ExposureDirectionType, string> = {
  North: 'N',
  'North East': 'NE',
  East: 'E',
  'South East': 'SE',
  South: 'S',
  'South West': 'SW',
  West: 'W',
  'North West': 'NW'
};

export const GOOGLE_MAP_LIBRARIES: Libraries = ['geometry', 'places'];

export const DETACHED_SPACE_CODES = new Set([
  'garage_detached',
  'office_detached',
  'workshop_detached'
]);
